import { makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';
import { AdvanceService } from '/src/api';
import { Advance, TAdvanceId } from '/src/lib/models';
import { CreateAdvanceForm, MenuItemType, TCreateAdvanceRequest, TListAdvancesRequest, UserRole } from '/src/lib/types';

export interface AdvanceStoreProps {
  /**
   * List of Advances
   */
  advances?: Advance[];
  /**
   * Currently selected Advance
   */
  currentAdvance?: Advance;
}

export class AdvanceStore extends BaseStore implements AdvanceStoreProps {
  private didInitMenu = false;
  advances?: Advance[];
  currentAdvance?: Advance;

  constructor(rootStore: RootStore) {
    super(rootStore);

    makeObservable(this, {
      advances: observable,
      currentAdvance: observable,
    });
  }

  /**
   * Adds Advance nav menu items to the app header.
   */
  initMenuItems = () => {
    if (this.didInitMenu || !this.rootStore.userStore.user) return;

    // Add menu items
    this.rootStore.menuStore.addMenuItem(
      'topbar',
      'Advances',
      'advances',
      this.rootStore.userStore.isLender ? MenuItemType.Item : MenuItemType.Dropdown,
      '/advances(/create)?',
      false,
      undefined,
      9
    );
    if (!this.rootStore.userStore.isLender) {
      this.rootStore.menuStore.addSubMenuItem('topbar', 'advances', 'List Advances', 'advances');
      this.rootStore.menuStore.addSubMenuItem(
        'topbar',
        'advances',
        'New Advance',
        'advances/create',
        'advances/create',
        false,
        [UserRole.Admin, UserRole.Company]
      );
    }

    runInAction(() => (this.didInitMenu = true));
  };

  /**** Data Processing ****/

  /**
   * Formats List Advances responses.
   *
   * @param advances - An array of Advances to format
   * @returns The formatted Advances
   */
  formatAdvanceList = (advances: Advance[]) => {
    return advances.map((advance) => this.formatAdvance(advance));
  };

  /**
   * Format Get Advance responses.
   *
   * @param advance - The Advance to format
   * @returns The formatted Advance
   */
  formatAdvance = (advance: Advance) => {
    // Get the asset object from the attached loan
    if (advance.loan?.assets && advance.loan.assets.length) {
      const asset = advance.loan.assets.find((asset) => asset._id === advance.assetId);

      if (asset) advance.asset = asset;
    }

    // Parse
    if (advance.dateRequested) advance.dateRequested = new Date(advance.dateRequested);
    if (advance.dateFulfilled) advance.dateFulfilled = new Date(advance.dateFulfilled);

    // Return
    return advance;
  };

  /**** Advance CRUD ****/

  /**
   * Fetches a list of Advances.
   *
   * @param query - Optional query to send with the API request
   * @returns An array of Advances
   */
  query = async (query?: TListAdvancesRequest) => {
    // if (query) query.px = true;
    const response = await AdvanceService.query(query);

    const advances = this.formatAdvanceList(response);

    runInAction(() => (this.advances = advances));

    return this.advances;
  };

  /**
   * Fetches a single Advance and sets it as the current Advance.
   *
   * @param advanceId - ID of the Advance to fetch
   * @returns An Advance object
   */
  get = async (advanceId: TAdvanceId) => {
    const response = await AdvanceService.get(advanceId);

    runInAction(() => {
      this.currentAdvance = response ? this.formatAdvance(response) : undefined;
    });

    return this.currentAdvance;
  };

  /**
   * Creates an Advance.
   *
   * @param formValues - A populated CreateAdvanceForm object
   * @returns The new Advance
   */
  create = async (formValues: CreateAdvanceForm) => {
    if (!formValues.loan || !formValues.company) throw new Error('Please fix the errors first.');

    // Create new Advance object
    const request: TCreateAdvanceRequest = {
      name: formValues.name,
      amount: formValues.amount,
      loan: formValues.loan._id,
      company: formValues.company._id,
      dateRequested: formValues.dateRequested,
      originationFee: formValues.amount * (formValues.loan.originationFee ?? 0),
      notes: formValues.notes,
      dateFulfilled: formValues.dateFulfilled,
      effectiveDate: formValues.dateFulfilled,
      files: formValues.files,
    };

    if (formValues.loan.isOwned && formValues.asset) request.assetId = formValues.asset._id;

    // Save the Advance
    const response = await AdvanceService.create(request);

    return response;
  };

  /**
   * Updates an Advance.
   *
   * @param advance - The Advance to update
   * @returns The updated Advance
   */
  update = async (advance: Advance) => {
    // Calculate
    if (advance.dateFulfilled) advance.effectiveDate = advance.dateFulfilled;
    if (advance.amount && advance.loan?.originationFee)
      advance.originationFee = advance.amount * advance.loan.originationFee;

    // Update
    const response = await AdvanceService.update(advance._id, advance);

    runInAction(() => (this.currentAdvance = Object.assign(this.currentAdvance ?? {}, response)));

    return this.currentAdvance;
  };

  /**
   * Removes an Advance.
   *
   * @param advanceId - ID of the Advance to delete
   * @returns The removed Advance
   */
  remove = async (advanceId: TAdvanceId) => {
    return await AdvanceService.remove(advanceId);
  };
}
